import { useEffect, useRef } from 'react';
import { featureUtil } from 'utils/geoJSONFeatureUtils/featureUtil';

import useMapContext from 'components/Map/hooks/useMapContext';
import useMapEvents from 'components/Map/hooks/useMapEvents';

import useMapbox from 'hooks/useMapBox';

import {
    CLICKABLE_LAYERS,
    CONTINUE_CHAIN,
    DEFAULT_CENTER,
    DRAWING_MODE,
    GEOJSON_PROPERTY,
    MAP_EVENT_TYPE,
    MAP_LAYER,
    STOP_CHAIN
} from './constants';
import type {
    Feature,
    HandleMapClickArgs,
    Map,
    MapVertexChangedEvent,
    Polygon,
    Position,
    Props
} from './types';

const useClick = ({
    boundaryPolygon,
    editMode,
    isDrawing,
    noEntryPolygons,
    onClick,
    onClickBoundaryLabel,
    removeBoundaryForFeature
}: Props) => {
    const { subscribe, unsubscribe } = useMapEvents();
    const { features, mapState, markers } = useMapContext();
    const {
        drawingMode,
        isPointSelected,
        mapErrors,
        selectedFeature,
        setDrawingMode,
        setDrawingTool,
        setSelectedFeature
    } = mapState;
    const { getFeatureById } = features;

    const { markerMap } = markers;

    const { getMapboxFeaturesUnderPoint } = useMapbox();

    const vertexPositionRef = useRef<Position>(DEFAULT_CENTER);

    useEffect(() => {
        const handleVertexChanged = (event: MapVertexChangedEvent) => {
            const { vertexPosition } = event.detail;

            vertexPositionRef.current = vertexPosition;
        };

        subscribe(MAP_EVENT_TYPE.MAP_VERTEX_CHANGED, handleVertexChanged);

        return () => {
            unsubscribe(MAP_EVENT_TYPE.MAP_VERTEX_CHANGED, handleVertexChanged);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectFeature = (feature?: Feature<Polygon>) => {
        if (!feature || !feature.properties) {
            return;
        }

        setSelectedFeature(feature);
        feature.properties[GEOJSON_PROPERTY.IS_SELECTED] = true;

        setDrawingTool(feature.properties?.[GEOJSON_PROPERTY.POLYGON_TYPE]);
        setDrawingMode(feature.properties?.[GEOJSON_PROPERTY.BOUNDARY_TYPE]);
    };

    const innerFeatureReducer = (acc: Feature[], feature: Feature) => {
        const innerFeatureId = feature.properties?.[GEOJSON_PROPERTY.INNER_FEATURE_ID];

        if (innerFeatureId) {
            const innerFeature = getFeatureById(innerFeatureId);

            if (innerFeature !== undefined) {
                acc.push(innerFeature);
            }
        }

        return acc;
    };

    const handleSelectMarker = (markerId?: string) => {
        if (!markerId) {
            return;
        }

        const marker = markerMap[markerId];

        if (!marker) {
            return;
        }
        const { featureId } = marker;
        const markerFeature = getFeatureById(featureId);

        if (
            markerFeature &&
            markerFeature.properties?.[GEOJSON_PROPERTY.IS_ACTIVE_BOUNDARY] === true
        ) {
            selectFeature(markerFeature as Feature<Polygon>);

            return;
        }
    };

    const handleSelectFeature = (featuresUnderClick: Feature[], latlng: Position) => {
        let featureToSelect: Feature<Polygon> | undefined;
        const innerFeatures = featuresUnderClick.reduce<Feature[]>(innerFeatureReducer, []);
        const activeFeaturesUnderClick = featuresUnderClick.filter(
            feature => feature.properties?.[GEOJSON_PROPERTY.IS_ACTIVE_BOUNDARY] === true
        );
        const allClickedFeatures = [...activeFeaturesUnderClick, ...innerFeatures];

        if (drawingMode === DRAWING_MODE.APPLY && boundaryPolygon) {
            featureToSelect = featureUtil.findFeatureWithId(
                allClickedFeatures,
                boundaryPolygon.id as string
            );
        } else if (drawingMode === DRAWING_MODE.NO_ENTRY && noEntryPolygons.length > 0) {
            featureToSelect = featureUtil.getClosestFeatureToPoint(
                allClickedFeatures,
                noEntryPolygons,
                latlng
            );
        }

        selectFeature(featureToSelect);
    };

    const handleDeselectFeature = () => {
        if (!selectedFeature) {
            return;
        }

        selectedFeature.properties = {
            ...selectedFeature.properties,
            [GEOJSON_PROPERTY.IS_SELECTED]: false
        };

        setSelectedFeature(undefined);
    };

    // Delete-mode branch (Decision 48). When `editMode === 'delete'`, a click
    // resolves to the closest NO_ENTRY feature under the cursor and deletes it.
    // Empty-space clicks are silently ignored so the user can deselect mis-clicks
    // without side effects. This handler sits at the HEAD of the click chain and
    // short-circuits before select / popover handlers ever run.
    const handleDeleteModeClick = (featuresUnderClick: Feature[], latlng: Position) => {
        if (editMode !== 'delete') {
            return CONTINUE_CHAIN;
        }

        const noEntryFeature = featureUtil.getClosestFeatureToPoint(
            featuresUnderClick,
            noEntryPolygons,
            latlng
        );

        if (!noEntryFeature) {
            return STOP_CHAIN;
        }

        removeBoundaryForFeature(noEntryFeature.id as string);

        return STOP_CHAIN;
    };

    const handleClusterClick = (featuresUnderClick: Feature[]) => {
        const didClickCluster = featuresUnderClick.some(
            feature =>
                feature.properties?.[GEOJSON_PROPERTY.CLUSTER] === true ||
                feature.properties?.[GEOJSON_PROPERTY.LAYER] === MAP_LAYER.CLUSTER
        );

        if (didClickCluster) {
            return STOP_CHAIN;
        }

        return CONTINUE_CHAIN;
    };

    const handleDeselectFeatureClick = (featuresUnderClick: Feature[], markerId?: string) => {
        const didClickFeature = featuresUnderClick.length > 0 || markerId;
        const isDeselectingFeature = selectedFeature && !didClickFeature;

        if (isDeselectingFeature && mapErrors.length > 0) {
            return STOP_CHAIN;
        }

        if (isDeselectingFeature) {
            handleDeselectFeature();

            return STOP_CHAIN;
        }

        return CONTINUE_CHAIN;
    };

    const handleSelectFeatureClick = (featuresUnderClick: Feature[], markerId?: string) => {
        const didClickFeature = featuresUnderClick.length > 0 || markerId;
        const isSameDrawingMode = featuresUnderClick.some(
            feature => feature.properties?.[GEOJSON_PROPERTY.BOUNDARY_TYPE] === drawingMode
        );

        const isSelectingMarker = markerId && !selectedFeature && !isDrawing;
        const isSelectingFeature =
            !selectedFeature && didClickFeature && !isDrawing && isSameDrawingMode;

        if (isSelectingMarker) {
            handleSelectMarker(markerId);

            return STOP_CHAIN;
        }

        if (isSelectingFeature) {
            handleSelectFeature(featuresUnderClick, vertexPositionRef.current);

            return STOP_CHAIN;
        }

        return CONTINUE_CHAIN;
    };

    const handleBoundaryLabelClick = (featuresUnderClick: Feature[], markerId?: string) => {
        const marker = markerId ? markerMap[markerId] : undefined;
        const isClickingPoint = featuresUnderClick.some(
            feature => feature.geometry.type === 'Point'
        );

        if (!marker) {
            return CONTINUE_CHAIN;
        }

        const { featureId, position } = marker;
        const markerFeature = getFeatureById(featureId);

        const isSelectedFeature =
            selectedFeature &&
            markerFeature &&
            markerFeature.id === selectedFeature.id &&
            selectedFeature.properties?.[GEOJSON_PROPERTY.IS_SELECTED] === true;

        const didClickBoundaryLabel =
            markerFeature &&
            markerFeature.properties?.[GEOJSON_PROPERTY.IS_ACTIVE_BOUNDARY] === true &&
            !isDrawing &&
            !isPointSelected &&
            !isClickingPoint &&
            isSelectedFeature;

        if (didClickBoundaryLabel) {
            onClickBoundaryLabel(position);

            return STOP_CHAIN;
        }

        return CONTINUE_CHAIN;
    };

    const handleDrawingClick = (featuresUnderClick: Feature[]) => {
        const isClickingPoint = featuresUnderClick.some(
            feature => feature.geometry.type === 'Point'
        );

        const isAttemptingToDrawBoundary =
            drawingMode === DRAWING_MODE.APPLY && boundaryPolygon && !isDrawing && !isClickingPoint;

        if (isAttemptingToDrawBoundary) {
            return STOP_CHAIN;
        }

        onClick(vertexPositionRef.current, featuresUnderClick);

        return STOP_CHAIN;
    };

    const handleMapClick = ({ hasDrawingControls, map, markerId }: HandleMapClickArgs) => {
        if (!map || !hasDrawingControls) {
            return;
        }

        const featuresUnderClick = getFeaturesUnderClick(map);

        const checkOrder = [
            () => handleDeleteModeClick(featuresUnderClick, vertexPositionRef.current),
            () => handleClusterClick(featuresUnderClick),
            () => handleDeselectFeatureClick(featuresUnderClick, markerId),
            () => handleSelectFeatureClick(featuresUnderClick, markerId),
            () => handleBoundaryLabelClick(featuresUnderClick, markerId),
            () => handleDrawingClick(featuresUnderClick)
        ];

        for (const check of checkOrder) {
            const result = check();

            if (result === STOP_CHAIN) {
                return;
            }
        }
    };

    const getFeaturesUnderClick = (map: Map): Feature[] => {
        const mapBoxFeatures = getMapboxFeaturesUnderPoint(map, vertexPositionRef.current);
        const mapBoxFeatureIds = mapBoxFeatures.map(feature => feature.id);

        const clickableLayers = CLICKABLE_LAYERS;

        const { features: allFeatures } = features;
        const clickedFeatures = allFeatures
            .filter(feature => mapBoxFeatureIds.includes(feature.id as string))
            .filter(feature =>
                clickableLayers.includes(feature.properties?.[GEOJSON_PROPERTY.LAYER])
            );

        return clickedFeatures;
    };

    return {
        handleMapClick,
        selectedFeature,
        setSelectedFeature
    };
};

export default useClick;
